import { useEffect, useRef } from "react";
import "../Why/Why.css";

const reasons = [
  {
    id: 1,
    icon: "⚖️",
    title: "Decades of Legal Mastery",
    desc: "Over 30 years of courtroom experience across High Courts and the Supreme Court of India, with a proven track record in complex litigation matters.",
    tag: "Litigation",
  },
  {
    id: 2,
    icon: "🏛️",
    title: "Pan-India Presence",
    desc: "With offices across Delhi, Mumbai, Bangalore, and Kolkata, we deliver swift, localized legal support wherever our clients need us most.",
    tag: "Nationwide",
  },
  {
    id: 3,
    icon: "🤝",
    title: "Client-First Philosophy",
    desc: "We treat every brief as a relationship, not a transaction. Your interests define our strategy, and your trust fuels our dedication.",
    tag: "Trust",
  },
  {
    id: 4,
    icon: "📜",
    title: "Corporate & Compliance Edge",
    desc: "From M&A due diligence to regulatory advisory, our corporate law division helps businesses navigate India's complex legal landscape.",
    tag: "Corporate",
  },
  {
    id: 5,
    icon: "🔒",
    title: "Confidential & Ethical",
    desc: "Bound by the highest ethical standards of the Bar Council of India, we ensure every matter is handled with absolute discretion.",
    tag: "Ethics",
  },
  {
    id: 6,
    icon: "🧠",
    title: "Strategic Legal Thinking",
    desc: "We go beyond case filing — our advisors craft long-term legal strategies that anticipate risk, protect assets, and safeguard your future.",
    tag: "Strategy",
  },
];

export default function WhyClientsChooseUs() {
  const sectionRef  = useRef(null);
  const titleRef    = useRef(null);
  const subtitleRef = useRef(null);
  const div1Ref     = useRef(null);
  const div2Ref     = useRef(null);
  const textRef     = useRef(null);
  const cardsRef    = useRef([]);
  const particleRef = useRef([]);
  const shimmerRef  = useRef(null);

  useEffect(() => {
    let gsap, ScrollTrigger;

    try {
      gsap = require("gsap").gsap ?? require("gsap");
      ({ ScrollTrigger } = require("gsap/ScrollTrigger"));
      gsap.registerPlugin(ScrollTrigger);
    } catch (e) {
      console.warn("GSAP not available", e);
      return;
    }

    const chars   = titleRef.current ? [...titleRef.current.querySelectorAll(".wc-char")] : [];
    const ps      = textRef.current  ? [...textRef.current.querySelectorAll("p")] : [];
    const cards   = cardsRef.current.filter(Boolean);

    /* — set initial hidden states via GSAP (NOT in CSS) — */
    gsap.set(subtitleRef.current, { autoAlpha: 0, y: 28, letterSpacing: "8px" });
    gsap.set(chars,               { autoAlpha: 0, y: 55, rotateX: -80 });
    gsap.set([div1Ref.current, div2Ref.current], { scaleX: 0, transformOrigin: "center" });
    gsap.set(ps,                  { autoAlpha: 0, y: 18 });
    gsap.set(cards,               { autoAlpha: 0, y: 55, scale: 0.9 });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sectionRef.current,
        start: "top 78%",
        once: true,
      },
    });

    // shimmer
    tl.fromTo(
      shimmerRef.current,
      { x: "-100%" },
      { x: "200%", duration: 1.5, ease: "power2.inOut" },
      0
    );

    // subtitle
    tl.to(
      subtitleRef.current,
      { autoAlpha: 1, y: 0, letterSpacing: "4px", duration: 0.7, ease: "power3.out" },
      0.15
    );

    // title chars
    tl.to(
      chars,
      { autoAlpha: 1, y: 0, rotateX: 0, duration: 0.55, ease: "back.out(1.7)", stagger: 0.028 },
      0.4
    );

    // dividers
    tl.to(
      [div1Ref.current, div2Ref.current],
      { scaleX: 1, duration: 0.65, ease: "power2.out" },
      1.05
    );

    // paragraphs
    tl.to(
      ps,
      { autoAlpha: 1, y: 0, duration: 0.65, stagger: 0.15, ease: "power2.out" },
      1.15
    );

    // cards
    tl.to(
      cards,
      { autoAlpha: 1, y: 0, scale: 1, duration: 0.6, stagger: 0.09, ease: "back.out(1.5)" },
      1.35
    );

    // floating particles
    particleRef.current.filter(Boolean).forEach((p, i) => {
      gsap.to(p, {
        y: -(18 + i * 7),
        x: Math.sin(i) * 11,
        duration: 2.8 + i * 0.35,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        delay: i * 0.25,
      });
    });

    // parallax bg
    gsap.to(sectionRef.current, {
      backgroundPositionY: "65%",
      ease: "none",
      scrollTrigger: {
        trigger: sectionRef.current,
        start: "top bottom",
        end: "bottom top",
        scrub: 1.5,
      },
    });

    // card hover
    cards.forEach((card) => {
      const onEnter = () => gsap.to(card, { y: -7, scale: 1.03, duration: 0.3, ease: "power2.out" });
      const onLeave = () => gsap.to(card, { y:  0, scale: 1,    duration: 0.3, ease: "power2.out" });
      card.addEventListener("mouseenter", onEnter);
      card.addEventListener("mouseleave", onLeave);
      card._gsapEnter = onEnter;
      card._gsapLeave = onLeave;
    });

    return () => {
      cards.forEach((card) => {
        card.removeEventListener("mouseenter", card._gsapEnter);
        card.removeEventListener("mouseleave", card._gsapLeave);
      });
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  const titleChars = "Why Clients Choose Us?".split("").map((ch, i) => (
    <span key={i} className="wc-char" style={{ whiteSpace: ch === " " ? "pre" : "normal" }}>
      {ch}
    </span>
  ));

  return (
    <section className="wc-section" ref={sectionRef}>
      <div className="wc-shimmer" ref={shimmerRef} />

      <div className="wc-particles">
        {[...Array(14)].map((_, i) => (
          <div
            key={i}
            className="wc-particle"
            ref={(el) => (particleRef.current[i] = el)}
            style={{
              width:   `${4 + (i % 4) * 3}px`,
              height:  `${4 + (i % 4) * 3}px`,
              left:    `${(i * 7.2) % 100}%`,
              top:     `${(i * 13 + 10) % 90}%`,
              opacity: 0.15 + (i % 5) * 0.08,
            }}
          />
        ))}
      </div>

      <div className="wc-outer">
        <div className="wc-border">
          <div className="wc-corner-tr" />
          <div className="wc-corner-bl" />

          <div className="wc-inner">
            <p className="wc-subtitle" ref={subtitleRef}>
              What We Are Expert At
            </p>

            <h1 className="wc-title" ref={titleRef}>
              {titleChars}
            </h1>

            <div className="wc-divider-wrap">
              <div className="wc-divider" ref={div1Ref} />
              <div className="wc-diamond" />
              <div className="wc-divider" ref={div2Ref} />
            </div>

            <div className="wc-text-cols" ref={textRef}>
              <p>
                With deep roots across India's legal system, our firm stands strong in delivering
                trusted counsel in areas such as litigation, corporate law, and dispute resolution.
                We work closely with clients to ensure their rights and interests are protected at
                every stage.
              </p>
              <p>
                Our legacy is built on excellence, ethics, and attention to detail. Whether advising
                a business on compliance or representing a client in court, we blend legal insight
                with dedication — ensuring thoughtful strategies and dependable outcomes.
              </p>
            </div>

            <div className="wc-cards-grid">
              {reasons.map((r, i) => (
                <div
                  key={r.id}
                  className="wc-card"
                  ref={(el) => (cardsRef.current[i] = el)}
                >
                  <span className="wc-card-icon">{r.icon}</span>
                  <span className="wc-card-tag">{r.tag}</span>
                  <div className="wc-card-title">{r.title}</div>
                  <div className="wc-card-desc">{r.desc}</div>
                  <div className="wc-card-line" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}